import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ExcelCommentBox.scss';

export interface ExcelComment {
  cellId: string;
  text: string;
  user: string | undefined;
  timestamp: number;
}

export interface ExcelCommentBoxProps {
  comment?: ExcelComment | null;
  user: string | undefined;
  cellId: string;
  onSave: (comment: ExcelComment) => void;
  onClose: () => void;
  position?: { top: number; left: number } | null;
  readOnly?: boolean;
}

const formatRelativeTime = (timestamp?: number): string => {
  if (!timestamp) return 'Now';

  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);

  if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''} ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours !== 1 ? 's' : ''} ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
};

const ExcelCommentBox: React.FC<ExcelCommentBoxProps> = ({
  comment = null,
  user,
  cellId,
  onSave,
  onClose,
  position = null,
  readOnly = false
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  const [text, setText] = useState('');
  const [isEditing, setIsEditing] = useState(!comment);

  //  Populate text if comment exists
  useEffect(() => {
    if (comment?.text) {
      setText(comment.text);
      setIsEditing(false);
    }
  }, [comment]);

  useEffect(() => {
    if (!popupRef.current || !position) return;

    const rect = popupRef.current.getBoundingClientRect();
    let { top, left } = position;

    if (top + rect.height > window.innerHeight) {
      top = window.innerHeight - rect.height - 10;
    }

    if (left + rect.width > window.innerWidth) {
      left = window.innerWidth - rect.width - 10;
    }

    popupRef.current.style.top = `${Math.max(10, top)}px`;
    popupRef.current.style.left = `${Math.max(10, left)}px`;
  }, [position]);

  //  Close when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (!popupRef.current) return;
      if (!popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('pointerdown', handleOutsideClick);

    return () => document.removeEventListener('pointerdown', handleOutsideClick);
  }, [onClose]);

  // Save comment
  const handleSave = () => {
    const newComment: ExcelComment = {
      cellId,
      text,
      user,
      timestamp: Date.now()
    };

    onSave(newComment);
    setIsEditing(false);
  };

  const isOwner = comment?.user === user;

  return createPortal(
    <>
      <div
        className="excel-comment-backdrop"
        onClick={onClose}
      />
      <div
        ref={popupRef}
        className="excel-comment-popup"
        style={{
          position: 'fixed',
          top: position?.top ?? 200,
          left: position?.left ?? 300,
          zIndex: 99999
        }}
      >
        <div className="excel-comment-header">
          <span className="excel-comment-user">
            {comment?.user ?? user}
          </span>

          <span className="excel-comment-time">
            {formatRelativeTime(comment?.timestamp)}
          </span>
        </div>

        <textarea
          className="excel-comment-textarea"
          placeholder="Add comment..."
          value={text}
          readOnly={readOnly || (!isEditing && !!comment)}
          onChange={(e) => setText(e.target.value)}
        />

        {/* ----- ACTION BUTTONS ----- */}

        {(isEditing || !comment) && (
          <div className="excel-comment-actions">
            <button
              type="button"
              className="excel-comment-save"
              onClick={handleSave}
            >
              Save
            </button>

            <button
              type="button"
              className="excel-comment-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Edit button only for owner */}
        {!isEditing && comment && isOwner && (
          <div className="excel-comment-actions">
            <button
              type="button"
              className="excel-comment-edit"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default ExcelCommentBox;
